import { useEffect, useState } from 'react';
import { Alert, Button, Empty, Select, Spin, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { listReleaseTree, type ModelReleaseDetail, type ModelReleaseRow } from '@apis/model';

export type ReleaseLoader = (name: string) => Promise<ModelReleaseDetail>;

interface Props {
  id?: string;
  value?: string;
  onChange?: (value?: string) => void;
  loadRelease: ReleaseLoader;
}

/** 值始终是完整发布名；详情属于已选值，异步返回不能覆盖后续选择。 */
export default function ModelReleasePicker({ id, value, onChange, loadRelease }: Props) {
  const [releases, setReleases] = useState<ModelReleaseRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [listRevision, setListRevision] = useState(0);
  const [detailRevision, setDetailRevision] = useState(0);
  const [detailState, setDetailState] = useState<{
    name?: string; detail?: ModelReleaseDetail; error?: string;
  }>({});

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(false);
    listReleaseTree().then(tree => {
      if (cancelled) return;
      const unique = new Map(tree.groups.flatMap(group => group.releases).map(release => [release.name, release]));
      setReleases([...unique.values()].sort((a, b) => b.create_time.localeCompare(a.create_time)));
    }).catch(() => {
      if (!cancelled) setListError(true);
    }).finally(() => {
      if (!cancelled) setListLoading(false);
    });
    return () => { cancelled = true; };
  }, [listRevision]);

  useEffect(() => {
    let cancelled = false;
    setDetailState({ name: value });
    if (value) {
      loadRelease(value).then(detail => {
        if (!cancelled) setDetailState({ name: value, detail });
      }).catch(() => {
        if (!cancelled) setDetailState({ name: value, error: '模型版本详情加载失败或版本已不存在，请重试或重新选择。' });
      });
    }
    return () => { cancelled = true; };
  }, [value, loadRelease, detailRevision]);

  const selectedDetail = detailState.name === value ? detailState.detail : undefined;
  const detailError = detailState.name === value ? detailState.error : undefined;
  const options = releases.map(release => ({
    value: release.name,
    label: release.name,
    title: release.description ?? release.name,
  }));
  // 复制过来的版本即使树加载失败也保留原值，由详情接口验证有效性。
  if (value && !options.some(option => option.value === value)) options.unshift({ value, label: value, title: value });

  return (
    <div className="job-model-release-picker">
      <div className="job-model-release-select">
        <Select
          id={id}
          value={value}
          onChange={onChange}
          showSearch
          allowClear
          optionFilterProp="label"
          placeholder="搜索完整模型发布版本名"
          loading={listLoading}
          options={options}
          notFoundContent={listLoading ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无匹配的模型版本" />}
          style={{ flex: 1, minWidth: 0 }}
        />
        <Button title="刷新模型版本列表" aria-label="刷新模型版本列表" icon={<ReloadOutlined />} loading={listLoading} onClick={() => setListRevision(revision => revision + 1)} />
      </div>
      {listError && <Alert type="warning" showIcon message="模型版本列表加载失败，点击刷新重试。" />}
      {value && !selectedDetail && !detailError && <div className="job-model-release-status"><Spin size="small" /> 正在加载版本内容…</div>}
      {detailError && (
        <Alert type="error" showIcon message={detailError} action={
          <Button size="small" onClick={() => setDetailRevision(revision => revision + 1)}>重试</Button>
        } />
      )}
      {selectedDetail && (
        <div className="job-model-release-preview">
          <div className="job-model-release-caption">
            版本内容 <Tag>{selectedDetail.manifest.items.length} 个模型</Tag>
          </div>
          {selectedDetail.description && <div className="job-custom-parameter-hint">{selectedDetail.description}</div>}
          <ul>
            {selectedDetail.manifest.items.map((item, index) => (
              <li key={`${item.name}:${item.version}:${index}`}>
                <strong>{item.name}</strong> <code>{item.version}</code>
                <span className="job-custom-parameter-hint">{[item.category, item.type].filter(Boolean).join(' · ')}</span>
                <div>{Object.keys(item.artifacts).map(profile => <Tag key={profile}>{profile}</Tag>)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
